import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Portal } from './Portal.jsx';
import { useAnchoredPopover } from './usePopover.js';
import {
  filterOptions,
  firstEnabled,
  firstMatchIndex,
  lastEnabled,
  nextEnabled,
  typeAheadIndex,
} from '../../lib/selectOptions.js';

const SEARCH_THRESHOLD = 10;

// The Steel Glass replacement for <select>: the same recessed 44px field as a
// trigger, and a menu panel (radius 12, 4px padding, 40px rows, a check on the
// selected row) instead of the browser's native list. Long lists get a search
// box. Values are strings, exactly as a native select reports them.
//
// `active` paints the trigger graphite: a list filter set to anything but its
// "All …" default, so what is filtering the list shows at a glance.
export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  ariaLabel,
  id,
  disabled = false,
  className = '',
  searchable,
  active = false,
  keepOnSearch = [],
}) {
  const reactId = useId();
  const baseId = id || `select-${reactId}`;
  const listId = `${baseId}-list`;
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const typeAhead = useRef({ text: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const stringValue = value == null ? '' : String(value);
  const selected = options.find((option) => option.value === stringValue);
  const withSearch = searchable ?? options.length > SEARCH_THRESHOLD;
  const shown = useMemo(
    () => (withSearch ? filterOptions(options, query, keepOnSearch) : options),
    [options, query, withSearch, keepOnSearch],
  );

  const style = useAnchoredPopover({
    open,
    onClose: (reason) => close(reason === 'escape'),
    anchorRef: triggerRef,
    popoverRef,
    matchWidth: true,
  });

  function openMenu() {
    if (disabled) return;
    setQuery('');
    const index = options.findIndex((option) => option.value === stringValue);
    setActiveIndex(index >= 0 ? index : firstEnabled(options));
    setOpen(true);
  }

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function choose(option) {
    if (!option || option.disabled) return;
    close(true);
    if (option.value !== stringValue) onChange(option.value);
  }

  // Focus the search box (or the list) once the menu is placed — it renders
  // hidden until then, and a hidden element cannot take focus.
  const placed = open && style.visibility !== 'hidden';
  useEffect(() => {
    if (!placed) return;
    (withSearch ? searchRef.current : listRef.current)?.focus({
      preventScroll: true,
    });
  }, [placed, withSearch]);

  // Keep the active row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document
      .getElementById(`${baseId}-opt-${activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, baseId]);

  function onListKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => nextEnabled(shown, i, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => nextEnabled(shown, i, -1));
    } else if (event.key === 'Home' && !withSearch) {
      event.preventDefault();
      setActiveIndex(firstEnabled(shown));
    } else if (event.key === 'End' && !withSearch) {
      event.preventDefault();
      setActiveIndex(lastEnabled(shown));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(shown[activeIndex]);
    } else if (event.key === 'Tab') {
      // Back on the trigger before the browser acts, so this Tab moves on
      // from it rather than from the menu at the end of <body>.
      close(true);
    } else if (!withSearch && event.key.length === 1) {
      const now = Date.now();
      const buffer =
        now - typeAhead.current.at > 600 ? '' : typeAhead.current.text;
      const text = buffer + event.key;
      typeAhead.current = { text, at: now };
      const index = typeAheadIndex(
        shown,
        text,
        text.length > 1 ? activeIndex - 1 : activeIndex,
      );
      if (index >= 0) setActiveIndex(index);
    }
  }

  function onTriggerKeyDown(event) {
    if (open) return;
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      openMenu();
    }
  }

  const activeId =
    open && activeIndex >= 0 && shown[activeIndex]
      ? `${baseId}-opt-${activeIndex}`
      : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        id={baseId}
        type="button"
        className={`select-trigger${active ? ' is-active' : ''}${selected ? '' : ' is-placeholder'} ${className}`.trim()}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        data-value={stringValue}
        disabled={disabled}
        onClick={() => (open ? close(true) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="select-trigger-label">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={20} aria-hidden="true" className="select-chevron" />
      </button>
      {open && (
        <Portal>
          <div
            ref={popoverRef}
            className="select-menu"
            style={style}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {withSearch && (
              <label className="select-search">
                <Search size={16} aria-hidden="true" />
                <input
                  ref={searchRef}
                  value={query}
                  placeholder="Search"
                  aria-label={`Search ${ariaLabel ? ariaLabel.toLowerCase() : 'options'}`}
                  aria-controls={listId}
                  aria-activedescendant={activeId}
                  autoComplete="off"
                  onChange={(event) => {
                    const next = event.target.value;
                    setQuery(next);
                    setActiveIndex(
                      firstMatchIndex(
                        filterOptions(options, next, keepOnSearch),
                        next,
                        keepOnSearch,
                      ),
                    );
                  }}
                  onKeyDown={onListKeyDown}
                />
              </label>
            )}
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              tabIndex={-1}
              aria-label={ariaLabel}
              aria-activedescendant={withSearch ? undefined : activeId}
              onKeyDown={withSearch ? undefined : onListKeyDown}
            >
              {shown.map((option, index) => {
                const isSelected = option.value === stringValue;
                return (
                  <li
                    key={option.value}
                    id={`${baseId}-opt-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    className={`${index === activeIndex ? 'is-active' : ''}${option.disabled ? ' is-disabled' : ''}`}
                    onMouseEnter={() =>
                      !option.disabled && setActiveIndex(index)
                    }
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(option)}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && (
                      <Check
                        size={16}
                        aria-hidden="true"
                        className="select-check"
                      />
                    )}
                  </li>
                );
              })}
              {!shown.length && (
                <li role="presentation" className="select-empty">
                  Nothing matches
                </li>
              )}
            </ul>
          </div>
        </Portal>
      )}
    </>
  );
}
